import { structure } from "@/endpoints/dryp/structure";
import { useAsyncStateStore } from "@/hooks/useAsyncStateStore";
import { useServerTable } from "@/hooks/useServerTable";
import { observer } from "mobx-react-lite";
import { useMemo } from "react";
import { getColumns } from "./Columns";
import { getTableStructureFilterParams } from "@/utils/filters";
import { StructureFilters } from "@/components/filters/StructureFilters";

import { ListMapView } from "@/components/ListMapView";
import { useMapStore } from "@/hooks/useMapStore";
import { MarkerDataConfig, MarkerLayerConfig } from "@/components/Map/stores/MapStore";
import { structureType } from "@/config/structureType";
import { DataDrivenPropertyValueSpecification } from "mapbox-gl";
import { SearchInput } from "@/components/SearchInput";
import { CUSTOM_FILTERS } from "@/config/list";
import { StructurePopUp } from "./StructurePopUp";
import { useStore } from "@/hooks/useStore";

export const structuresLayerConfig: MarkerLayerConfig = (() => {
	const matchExpr: any[] = ["match", ["get", "structureTypeId"]];

	for (const [id, cfg] of Object.entries(structureType)) {
		matchExpr.push(id, cfg.bgColor);
	}

	matchExpr.push("#9CA3AF");

	return {
		paint: {
			"circle-radius": 6,
			"circle-color": matchExpr as unknown as DataDrivenPropertyValueSpecification<string>,
			"circle-stroke-color": "#ffffff",
			"circle-stroke-width": 1,
		},
	};
})();

type StructureItem = NonNullable<Awaited<ReturnType<typeof structure.getAllStructures>>["items"]>[number];

export const structuresDataConfig: MarkerDataConfig<StructureItem> = {
	getCoordinates: s => [s.coordinate.longitude, s.coordinate.latitude],
	getId: s => String(s.id),
	getProperties: structure => ({
		structureTypeId: String(structure.type.id),
	}),
};

export const Structures = observer(() => {
	const structuresApi = useAsyncStateStore(structure.getAllStructures, { debounce: 300 });
	const mapStructuresApi = useAsyncStateStore(structure.getAllStructures, { debounce: 300 });

	const {
		uiStore: { modalStore },
		userStore,
	} = useStore();

	const canManageIAndI = userStore.isAdmin || userStore.isSuperAdmin;

	const columns = useMemo(() => getColumns({ modalStore, canManageIAndI }), [modalStore, canManageIAndI]);

	const { table } = useServerTable({
		columns,
		data: structuresApi.data?.items || [],
		fetchFn: params => {
			const structureFilters = getTableStructureFilterParams(params?.filters);
			return structuresApi.fetch(
				{ pageNumber: params.page, pageSize: params.pageSize },
				structureFilters,
				params.sort,
			);
		},
		initialValues: {
			page: 0,
			pageSize: 10,
			columnVisibility: {
				id: false,
				hasBlockage: false,
				hasIAndI: false,
				hasIAndISensors: false,
			},
		},
		enableUrlParams: true,
		initFromUrl: true,
	});

	const mapStore = useMapStore(structuresLayerConfig, structuresDataConfig, {
		clusterRadius: 30,
		clusterMaxZoom: 11,
	});

	mapStore.onFetch = async () => {
		const structureFilters = getTableStructureFilterParams(mapStore.filters.getFilters());

		await mapStructuresApi.fetch(
			{
				pageNumber: 0,
				pageSize: 100000,
			},
			structureFilters,
			undefined,
		);

		if (!mapStore.isLoaded) return;
		const items = mapStructuresApi.data?.items || [];
		mapStore.setMarkers(items);
		mapStore.fitToData(items, s => [s.coordinate.longitude, s.coordinate.latitude]);
	};

	return (
		<div className="flex flex-col gap-4 md:gap-6 p-4 md:p-6 min-h-full overflow-hidden">
			<header className="flex items-center justify-between">
				<h1 className="text-2xl md:text-4xl font-semibold tracking-tight">Structures</h1>
			</header>

			<ListMapView
				filters={(filter, _view) => (
					<div className="flex flex-wrap md:flex-nowrap gap-2 items-end">
						<SearchInput
							value={(filter.getFilter(CUSTOM_FILTERS.search) as string | undefined) ?? ""}
							onChange={value => filter.setFilter(CUSTOM_FILTERS.search, value)}
						/>

						<StructureFilters filter={filter} />
					</div>
				)}
				list={{
					table,
					layout: {
						loading: structuresApi.loading,
					},
					pagination: {
						total: structuresApi.data?.query?.pagination?.totalItems || 0,
					},
				}}
				initFromUrl={true}
				enableUrlParams={true}
				map={{
					mapStore,
					loader: { isLoading: mapStructuresApi.loading },
					renderPopup: structure => (
						<StructurePopUp
							structure={structure}
							selectedTypeIds={mapStore.filters.selectedIds(CUSTOM_FILTERS.structureTypesIds)}
							selectedGroupIds={mapStore.filters.selectedIds(CUSTOM_FILTERS.structureGroupIds)}
						/>
					),
					enableUrlParams: true,
					initFromUrl: true,
				}}
			/>
		</div>
	);
});
